import React from "react";
import { motion } from "framer-motion";
import { ArrowUp, ArrowDown, Equals, Warning } from "@phosphor-icons/react";

const LEVER_LABEL = {
  dream_outcome: "Dream outcome",
  perceived_likelihood: "Perceived likelihood",
  time_delay: "Time delay",
  effort_and_sacrifice: "Effort & sacrifice",
};

const STATUS_COPY = {
  improved: "moved",
  held_at_ceiling: "already maxed — held",
  unchanged: "did not move",
  regressed: "went backwards",
};

function Bar({ value, good }) {
  return (
    <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full ${good ? "bg-[#C9A961]" : "bg-[#5A6577]"}`}
        style={{ width: `${(value / 10) * 100}%` }}
      />
    </div>
  );
}

/**
 * The before/after on the four value-equation levers.
 *
 * This is the piece that makes the tool answerable: the original offer and the
 * reworked one were each scored on the same 1-10 rubric by a scorer that saw one
 * offer at a time, and the value score is arithmetic, not opinion.
 */
export default function ValueLift({ proof }) {
  if (!proof || !proof.moves) return null;

  const up = proof.value_score_after > proof.value_score_before;

  return (
    <section className="mt-20" data-testid="value-lift-section">
      <div className="flex items-center gap-3 mb-2">
        {up ? (
          <ArrowUp weight="fill" className="h-5 w-5 text-[#C9A961]" />
        ) : (
          <Warning weight="fill" className="h-5 w-5 text-[#E0B64B]" />
        )}
        <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">
          What actually changed
        </h2>
      </div>
      <p className="text-[#9AA5B5] max-w-3xl mb-8">
        Your original offer and this one were each scored 1–10 on the same four
        levers. Value score is{" "}
        <span className="text-[#F5F1E8]">
          (dream outcome × perceived likelihood) ÷ (time delay × effort)
        </span>
        . Time delay and effort measure how big the delay and the burden are, so
        lower is better on those two.
      </p>

      <div className="grid grid-cols-12 gap-4 mb-8">
        <div
          className="col-span-12 md:col-span-4 bg-[#0F1F38] border border-white/5 rounded-lg p-8"
          data-testid="value-score-card"
        >
          <div className="text-xs tracking-[0.25em] uppercase text-[#C9A961] font-bold mb-4">
            Value score
          </div>
          <div className="flex items-baseline gap-3">
            <span className="font-display text-3xl text-[#5A6577] line-through">
              {proof.value_score_before}
            </span>
            <ArrowUp weight="bold" className="h-5 w-5 text-[#C9A961]" />
            <span className="font-display text-5xl font-black text-[#C9A961]">
              {proof.value_score_after}
            </span>
          </div>
          {proof.multiple > 0 && (
            <div className="mt-3 text-sm text-[#9AA5B5]">
              {proof.multiple}× the original
            </div>
          )}
        </div>

        <div className="col-span-12 md:col-span-8 bg-[#0F1F38] border border-white/5 rounded-lg p-8">
          <div className="text-xs tracking-[0.25em] uppercase text-[#C9A961] font-bold mb-4">
            Honesty check
          </div>
          <p className="text-[#F5F1E8] leading-relaxed">{proof.note}</p>
          {!proof.all_levers_moved && (
            <p className="mt-3 text-sm text-[#E0B64B]">
              A lever that did not move is a real gap, not a rounding error — use
              Refine and tell it what you can genuinely change about delivery.
            </p>
          )}
        </div>
      </div>

      <div className="bg-[#0F1F38] border border-white/5 rounded-lg divide-y divide-white/5">
        {proof.moves.map((m, i) => {
          const down = m.direction === "down";
          const Icon =
            m.status === "improved"
              ? down
                ? ArrowDown
                : ArrowUp
              : m.status === "regressed"
              ? Warning
              : Equals;
          return (
            <motion.div
              key={m.lever}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              className="grid grid-cols-12 gap-6 p-6 md:p-8 items-start"
              data-testid={`lever-move-${m.lever}`}
            >
              <div className="col-span-12 md:col-span-3">
                <div className="font-display font-bold text-white mb-1">
                  {LEVER_LABEL[m.lever] || m.lever}
                </div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#808A9B]">
                  {down ? "lower is better" : "higher is better"}
                </div>
              </div>

              <div className="col-span-12 md:col-span-3">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-[#5A6577] font-display text-xl">
                    {m.before}
                  </span>
                  <Icon
                    weight="bold"
                    className={`h-4 w-4 ${
                      m.moved ? "text-[#C9A961]" : "text-[#808A9B]"
                    }`}
                  />
                  <span className="text-[#C9A961] font-display text-2xl font-black">
                    {m.after}
                  </span>
                  <span className="text-xs text-[#808A9B]">
                    {STATUS_COPY[m.status] || m.status}
                  </span>
                </div>
                <Bar value={m.after} good={m.moved} />
              </div>

              <div className="col-span-12 md:col-span-6">
                <p className="text-[#F5F1E8] leading-relaxed">
                  {m.what_changed || "—"}
                </p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
